import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { PageHeader, Spinner, EmptyState } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

/** manager_employee, only reachable when the dispatcher granted this
 * manager can_delegate_manage_companies: lets them grant/revoke the
 * ability to define new companies/users for employees in their own
 * department. Delegated employees can't delegate further (single level). */
export default function ManageCompanyDelegationPage() {
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await departmentApi.getDepartmentEmployeesPermissions();
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else if (showSpinner) addToast('خطا در بارگذاری فهرست کارمندان', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!saving });

  const toggle = async (employee) => {
    const next = !employee.can_manage_companies;
    setSaving(employee.employee_id);
    try {
      await departmentApi.setEmployeeCompanyPermission({
        employee_id: employee.employee_id,
        can_manage_companies: next ? 1 : 0,
      });
      setEmployees((prev) => prev.map((e) => (e.employee_id === employee.employee_id ? { ...e, can_manage_companies: next } : e)));
    } catch {
      addToast('خطا در ذخیره دسترسی', 'error');
    }
    setSaving(null);
  };

  if (!loading && (forbidden || !user?.can_delegate_manage_companies)) {
    return (
      <div className="fade-in">
        <PageHeader title="واگذاری دسترسی تعریف شرکت/کاربر" action={<Link to="/settings" style={{ fontSize: 13, color: '#64748b' }}>بازگشت به تنظیمات</Link>} />
        <EmptyState title="دسترسی ندارید" subtitle="دیسپچر هنوز اجازه واگذاری این دسترسی را به شما نداده است." />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <PageHeader
        title="واگذاری دسترسی تعریف شرکت/کاربر"
        subtitle="کدام‌یک از کارمندان دپارتمان شما اجازه دارند شرکت یا کاربر جدید تعریف کنند"
        action={<Link to="/settings" style={{ fontSize: 13, color: '#64748b' }}>بازگشت به تنظیمات</Link>}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : !employees.length ? (
        <div style={{ fontSize: 13, color: '#94a3b8' }}>کارمند دیگری در این دپارتمان یافت نشد.</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', maxWidth: 560 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ textAlign: 'right', padding: '10px 16px', color: '#64748b', fontWeight: 600 }}>کارمند</th>
                <th style={{ padding: '10px 16px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>تعریف شرکت/کاربر</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.employee_id} style={{ borderTop: '1px solid #f1f5f9', opacity: saving === e.employee_id ? 0.5 : 1 }}>
                  <td style={{ padding: '10px 16px', fontWeight: 600, color: '#0f172a' }}>{`${e.first_name || ''} ${e.last_name || ''}`.trim() || '—'}</td>
                  <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                    <input
                      type="checkbox"
                      disabled={saving === e.employee_id}
                      checked={e.can_manage_companies}
                      onChange={() => toggle(e)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
